import { Color4, Vector2, Vector3, TmpVectors, Quaternion } from "../Maths/math.js";
/**
 * Represents one particle of a points cloud system.
 */
var CloudPoint = /** @class */ (function () {
    /**
     * Creates a Point Cloud object.
     * Don't create particles manually, use instead the PCS internal tools like _addParticle()
     * @param particleIndex (integer) is the particle index in the PCS pool. It's also the particle identifier.
     * @param group (PointsGroup) is the group the particle belongs to
     * @param groupId (integer) is the group identifier in the PCS.
     * @param idxInGroup (integer) is the index of the particle in the current point group (ex: the 10th point of addPoints(30))
     * @param pcs defines the PCS it is associated to
     */
    function CloudPoint(particleIndex, group, groupId, idxInGroup, pcs) {
        /**
         * particle global index
         */
        this.idx = 0;
        /**
         * The color of the particle
         */
        this.color = new Color4(1.0, 1.0, 1.0, 1.0);
        /**
         * The world space position of the particle.
         */
        this.position = Vector3.Zero();
        /**
         * The world space rotation of the particle. (Not use if rotationQuaternion is set)
         */
        this.rotation = Vector3.Zero();
        /**
         * The uv of the particle.
         */
        this.uv = new Vector2(0.0, 0.0);
        /**
         * The current speed of the particle.
         */
        this.velocity = Vector3.Zero();
        /**
         * The pivot point in the particle local space.
         */
        this.pivot = Vector3.Zero();
        /**
         * Must the particle be translated from its pivot point in its local space ?
         * In this case, the pivot point is set at the origin of the particle local space and the particle is translated.
         * Default : false
         */
        this.translateFromPivot = false;
        /**
         * Index of this particle in the global "positions" array (Internal use)
         * @hidden
         */
        this._pos = 0;
        /**
         * @hidden Index of this particle in the global "indices" array (Internal use)
         */
        this._ind = 0;
        /**
         * Group id of this particle
         */
        this.groupId = 0;
        /**
         * Index of the particle in its group id (Internal use)
         */
        this.idxInGroup = 0;
        /**
         * @hidden Still set as invisible in order to skip useless computations (Internal use)
         */
        this._stillInvisible = false;
        /**
         * @hidden Last computed particle rotation matrix
         */
        this._rotationMatrix = [1.0, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 1.0];
        /**
         * Parent particle Id, if any.
         * Default null.
         */
        this.parentId = null;
        /**
         * @hidden Internal global position in the PCS.
         */
        this._globalPosition = Vector3.Zero();
        this.idx = particleIndex;
        this._group = group;
        this.groupId = groupId;
        this.idxInGroup = idxInGroup;
        this._pcs = pcs;
    }
    Object.defineProperty(CloudPoint.prototype, "size", {
        /**
         * get point size
         */
        get: function () {
            return this.size;
        },
        /**
         * Set point size
         */
        set: function (scale) {
            this.size = scale;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CloudPoint.prototype, "quaternion", {
        /**
         * Legacy support, changed quaternion to rotationQuaternion
         */
        get: function () {
            return this.rotationQuaternion;
        },
        /**
         * Legacy support, changed quaternion to rotationQuaternion
         */
        set: function (q) {
            this.rotationQuaternion = q;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Returns a boolean. True if the particle intersects a mesh, else false
     * The intersection is computed on the particle position and Axis Aligned Bounding Box (AABB) or Sphere
     * @param target is the object (point or mesh) what the intersection is computed against
     * @param isSphere is boolean flag when false (default) bounding box of mesh is used, when true the bounding sphere is used
     * @returns true if it intersects
     */
    CloudPoint.prototype.intersectsMesh = function (target, isSphere) {
        if (!target.hasBoundingInfo) {
            return false;
        }
        isSphere = isSphere ? isSphere : false;
        if (isSphere) {
            return target.getBoundingInfo().boundingSphere.intersectsPoint(this.position.add(this._pcs.mesh.position));
        }
        else {
            var maxX = 0;
            var minX = 0;
            var maxY = 0;
            var minY = 0;
            var maxZ = 0;
            var minZ = 0;
            maxX = target.getBoundingInfo().boundingBox.maximumWorld.x;
            minX = target.getBoundingInfo().boundingBox.minimumWorld.x;
            maxY = target.getBoundingInfo().boundingBox.maximumWorld.y;
            minY = target.getBoundingInfo().boundingBox.minimumWorld.y;
            maxZ = target.getBoundingInfo().boundingBox.maximumWorld.z;
            minZ = target.getBoundingInfo().boundingBox.minimumWorld.z;
            var x = this.position.x + this._pcs.mesh.position.x;
            var y = this.position.y + this._pcs.mesh.position.y;
            var z = this.position.z + this._pcs.mesh.position.z;
            return minX <= x && x <= maxX && minY <= y && y <= maxY && minZ <= z && z <= maxZ;
        }
    };
    /**
     * get the rotation matrix of the particle
     * @param m
     * @hidden
     */
    CloudPoint.prototype.getRotationMatrix = function (m) {
        var quaternion;
        if (this.rotationQuaternion) {
            quaternion = this.rotationQuaternion;
        }
        else {
            quaternion = TmpVectors.Quaternion[0];
            var rotation = this.rotation;
            Quaternion.RotationYawPitchRollToRef(rotation.y, rotation.x, rotation.z, quaternion);
        }
        quaternion.toRotationMatrix(m);
    };
    return CloudPoint;
}());
export { CloudPoint };
/**
 * Represents a group of points in a points cloud system
 *  * PCS internal tool, don't use it manually.
 */
var PointsGroup = /** @class */ (function () {
    /**
     * Creates a points group object. This is an internal reference to produce particles for the PCS.
     * PCS internal tool, don't use it manually.
     * @param id
     * @param posFunction
     * @hidden
     */
    function PointsGroup(id, posFunction) {
        this.groupId = id;
        this._positionFunction = posFunction;
    }
    Object.defineProperty(PointsGroup.prototype, "groupID", {
        /**
         * Get or set the groupId
         * @deprecated Please use groupId instead
         */
        get: function () {
            return this.groupId;
        },
        set: function (groupID) {
            this.groupId = groupID;
        },
        enumerable: false,
        configurable: true
    });
    return PointsGroup;
}());
export { PointsGroup };
//# sourceMappingURL=cloudPoint.js.map